import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CartService } from '@core/services/cart/cart.service';
import { TranslateService } from '@ngx-translate/core';
import { CURRENCY_SYMBOL } from '@shared/utils/currency/currency-symbol';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { PrepaymentSelectorService } from '@common/selectors/prepayment.selector';

@Injectable()
export class TopUpService {
  private readonly cartService = inject(CartService);
  private readonly translateService = inject(TranslateService);
  public readonly currencySymbol = inject(CURRENCY_SYMBOL);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly cartStoreSelector = inject(CartStoreSelector);
  private readonly prepaymentSelectorService = inject(PrepaymentSelectorService);

  public readonly events = signal<
    | {
        type: 'Top up added';
      }
    | {
        type: 'Top up removed';
      }
    | undefined
  >(undefined);

  public readonly inputValue = signal<number | null>(null);

  public readonly preSelectedAmountInCents = computed(() => {
    return this.cartStoreSelector.topUpProductInCart()?.amount;
  });

  public readonly selectedAmount = computed(() => {
    return signal((this.preSelectedAmountInCents() ?? 0) / 100);
  });

  public readonly isAmountValid = computed(() => {
    const amount = this.inputValue();
    if (amount === null || amount === 0) return true; // Pas d'erreur si vide
    const minAmount = (this.minTopUpAmount() ?? 0) / 100;
    const maxAmount = (this.maxTopUpAmount() ?? 0) / 100;
    return amount >= minAmount && amount <= maxAmount;
  });

  public readonly errorMessage = computed(() => {
    const amount = this.inputValue();
    if (amount === null || amount === 0 || this.isAmountValid()) return null;

    const minAmount = (this.minTopUpAmount() ?? 0) / 100;
    const maxAmount = (this.maxTopUpAmount() ?? 0) / 100;

    if (amount < minAmount) {
      return this.translateService.instant('prepaid-account-top-up-modal.minimum-amount-error', {
        amount: `${minAmount}${this.currencySymbol}`,
      });
    }
    if (amount > maxAmount) {
      return this.translateService.instant('prepaid-account-top-up-modal.maximum-amount-error', {
        amount: `${maxAmount}${this.currencySymbol}`,
      });
    }
    return null;
  });

  public readonly orderBalanceBeforeOrder =
    this.prepaymentSelectorService.selectBalanceBeforeOrderInCents;

  public readonly priceToDisplay =
    this.prepaymentSelectorService.selectTopUpPreselectedAmountInCents;

  public readonly minTopUpAmount = this.prepaymentSelectorService.selectTopUpAmountMinInCents;

  public readonly maxTopUpAmount = this.prepaymentSelectorService.selectTopUpAmountMaxInCents;

  public readonly isDesktop = toSignal(this.settingsStoreSelector.selectDeviceIsDesktopScreen);

  public onSelectedAmount(amount: number): void {
    this.selectedAmount().set(amount);
    this.inputValue.set(amount); // Synchroniser avec l'input
  }

  public onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = parseFloat(target.value);

    if (isNaN(value) || target.value === '') {
      this.inputValue.set(null);
      this.selectedAmount().set(0);
    } else {
      this.inputValue.set(value);
      this.selectedAmount().set(value);
    }
  }

  public onKeyDown(event: KeyboardEvent, amount: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSelectedAmount(amount);
    }
  }

  public onTopUp(): void {
    this.cartService.changeTopUpAmount$((this.selectedAmount()() ?? 0) * 100).subscribe();
    this.events.set({ type: 'Top up added' });
  }

  public remove(): void {
    const cartItemId = this.cartStoreSelector.topUpProductInCart()?.item_id;
    if (!cartItemId) {
      return;
    }
    this.cartService.removeItem$(cartItemId).subscribe();
    this.events.set({ type: 'Top up removed' });
  }
}
